<template>
  <div class="part2-main-machine" ref="targetRef">
    <div class="egg-text">
      <div class="egg-text-title">
        <i class="egg-text-title-icon"></i>
        <span class="egg-text-title-text">每次抽獎消耗：20積分</span>
      </div>
      <div class="egg-text-title-text btns" @click="showBag">
        查看我的獎品 >>
      </div>
    </div>
    <div class="egg-container j-egg-container" @click="showPoolInfo">
      <i class="egg egg-1 j-egg-1"></i>
      <i class="egg egg-2 j-egg-2"></i>
      <i class="egg egg-3 j-egg-3"></i>
      <i class="egg egg-4 j-egg-4"></i>
      <i class="egg egg-5 j-egg-5"></i>
      <i class="egg egg-6 j-egg-6"></i>
      <i class="egg egg-7 j-egg-7"></i>
      <i class="egg egg-8 j-egg-8"></i>
      <i class="egg egg-9 j-egg-9"></i>
      <i class="egg egg-10 j-egg-10"></i>
      <i class="egg egg-11 j-egg-11"></i>
      <i class="egg egg-12 j-egg-12"></i>
      <i class="egg egg-13 j-egg-13"></i>
      <i class="egg egg-14 j-egg-14"></i>
      <i class="egg egg-15 j-egg-15"></i>
      <i class="egg egg-16 j-egg-16"></i>
      <i class="egg egg-17 j-egg-17"></i>
      <i class="egg egg-18 j-egg-18"></i>
      <i class="egg egg-19 j-egg-19"></i>
      <i class="egg egg-20 j-egg-20"></i>
    </div>
    <i class="egg-show j-egg-show"></i>

    <!-- 抽奖按钮 -->
    <div class="egg-btns" v-if="showLotteryButtons">
      <div
        class="start-1 btns"
        :style="{
          filter: canStart1Pull ? 'none' : 'grayscale(1)',
          opacity: canStart1Pull ? '1' : '0.6',
          cursor: canStart1Pull ? 'pointer' : 'not-allowed'
        }"
        @click="start('1_pull')"
      >
        <span class="text">單次抽獎</span>
      </div>
      <div
        class="start-10 btns"
        :style="{
          filter: canStart10Pull ? 'none' : 'grayscale(1)',
          opacity: canStart10Pull ? '1' : '0.6',
          cursor: canStart10Pull ? 'pointer' : 'not-allowed'
        }"
        @click="start('10_pull')"
      >
        <span class="text">10次抽獎</span>
      </div>
    </div>

    <!-- 提示文案 -->
    <div class="egg-tips" v-if="showLotteryTips">請先消耗積分，完成抽獎后即可領獎噢~</div>

    <!-- 领奖按钮 -->
    <div
      class="get-award-btn btns"
      @click="claimAward"
      v-if="showGetAwardButton"
    ></div>
  </div>
</template>

<script setup>
import Animation from "@/utils/lottery-animation";
import {
  ref,
  onMounted,
  onUnmounted,
  inject,
  reactive,
  nextTick,
  computed,
} from "vue";
import useViewPagePoint from "@/hooks/useViewPagePoint";

import { getJSON } from "@/api/request";
import { getActivityApi, pointClickButton } from "@/utils";
import { mockLotteryResult, mockDelay } from "@/api/mock";
import { UgcTab } from "../typings";
import { lotteryAwardList, lotteryResultPopupState } from "@/hooks/useGetAward";
import { getRewardByConfigId } from "@/config/rewards";

// 页面视图打点
const { targetRef } = useViewPagePoint("积分抽奖");

const v3PopupState = inject("v3PopupState", reactive({}));
const lotteryResult = inject("lotteryResult", ref([]));
const showCurErrorTips = inject("showCurErrorTips", () => {});
const showTipMessage = inject("showTipMessage", () => {});
const checkLogin = inject("checkLogin", () => {});
const initActivity = inject("initActivity", () => {});
const lotteryAndTaskState = inject("lotteryAndTaskState", reactive({}));
const enableMock = inject("enableMock", false);

let animation = null;

let canStart = ref(true);

// 抽奖积分配置
const LOTTERY_COST = {
  single: 20,  // 单抽消耗 20 积分
  ten: 200,    // 十连抽消耗 200 积分
};

// 计算单次抽奖是否可用
const canStart1Pull = computed(() => {
  // 领奖期已结束，不可抽奖
  if (lotteryAndTaskState.awardIsEnd === 1) {
    return false;
  }
  // 积分足够
  return lotteryAndTaskState.totalScore >= LOTTERY_COST.single;
});

// 计算十连抽是否可用
const canStart10Pull = computed(() => {
  // 领奖期已结束，不可抽奖
  if (lotteryAndTaskState.awardIsEnd === 1) {
    return false;
  }
  // 积分足够
  return lotteryAndTaskState.totalScore >= LOTTERY_COST.ten;
});

// 计算是否显示抽奖按钮
const showLotteryButtons = computed(() => {
  // 领奖期开始 且 积分不足，不显示抽奖按钮（显示领奖按钮）
  if (lotteryAndTaskState.awardIsStart === 1 && lotteryAndTaskState.totalScore < LOTTERY_COST.single) {
    return false;
  }
  // 其他情况显示抽奖按钮
  return true;
});

// 计算是否显示领奖按钮
const showGetAwardButton = computed(() => {
  // 领奖期已开始 且 积分已消耗完（< 20）
  return lotteryAndTaskState.awardIsStart === 1 && lotteryAndTaskState.totalScore < LOTTERY_COST.single;
});

// 计算是否展示提示文案
const showLotteryTips = computed(() => {
  // 领奖期已开始 且 还有积分（>= 20）
  return lotteryAndTaskState.awardIsStart === 1 && lotteryAndTaskState.totalScore >= LOTTERY_COST.single;
});

const start = async (type) => {
  if (!checkLogin()) {
    return;
  }

  if (!canStart.value) return;

  const lotteryCount = type === "1_pull" ? 1 : 10;
  const requiredScore = lotteryCount === 1 ? LOTTERY_COST.single : LOTTERY_COST.ten;

  // 检查是否可以抽奖（领奖期已结束 或 积分不足）
  if (lotteryAndTaskState.awardIsEnd === 1) {
    showTipMessage('領獎期已結束，無法抽獎');
    return;
  }

  if (lotteryAndTaskState.totalScore < requiredScore) {
    showTipMessage('積分不足');
    return;
  }

  // 打点上报
  pointClickButton(
    lotteryCount === 1 ? "单次抽奖按钮" : "十连抽奖按钮",
    "积分抽奖"
  );

  canStart.value = false;

  window.activityDataLayer.push({
    eventName: "click_button",
    eventType: type == "1_pull" ? "单次抽奖" : "10次抽獎",
    eventReason: "首页",
    aid: window.activity_name,
    gameId: window.game_id,
  });

  animation.reset();

  let res;

  if (enableMock) {
    // 🎭 Mock 模式
    console.log(`🎭 Mock: ${lotteryCount}次抽奖`);
    await mockDelay(500);
    res = mockLotteryResult(lotteryCount);
  } else {
    // 真实接口
    res = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "lottery"),
      {
        params: JSON.stringify({
          lotteryType: type,
        }),
      }
    );
  }

  if (res.result != 1) {
    showCurErrorTips(res.code);
    canStart.value = true;
    return;
  }

  // 处理抽奖结果，添加奖励信息
  const results = res.data.results || [];
  lotteryAwardList.value = results.map((item) => {
    const rewardInfo = getRewardByConfigId(item.awardId);
    return {
      ...item,
      icon: rewardInfo?.icon || '',
      name: rewardInfo?.name || '未知獎勵',
    };
  });

  lotteryResult.value = res.data.lotteryAwardList || [];

  // 播放动画
  await animation.start();
  animation.reset();

  // 显示抽奖结果弹窗
  lotteryResultPopupState.value = true;

  await initActivity();
  canStart.value = true;
};

const showBag = () => {
  if (!checkLogin()) {
    return;
  }
  pointClickButton("我的奖品", "积分抽奖");
  v3PopupState.bagPopupState = true;
};

const showPoolInfo = () => {
  pointClickButton("奖池信息", "积分抽奖");
  v3PopupState.poolPopupState = true;
};

// 领奖接口
const claimAward = async () => {
  if (!checkLogin()) {
    return;
  }

  pointClickButton("领取奖励按钮", "积分抽奖");

  try {
    const res = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "claimMyBackpack"),
      {}
    );

    if (res.result === 1) {
      // 刷新活动数据
      await initActivity();
      // 打开背包查看奖励
      showBag();
    } else {
      showCurErrorTips(res.code);
    }
  } catch (error) {
    console.error('❌ 领奖失败:', error);
  }
};

onMounted(async () => {
  await nextTick();
  animation = new Animation();
});

onUnmounted(() => {
  animation.destroy();
  animation = null;
});
</script>

<style scoped lang="less"></style>
